import {
  REST_ID,
  blockHas,
  blockName,
  chainScheme,
  clientLine,
  fmtDesc,
  fmtLabel,
  isTextBlock,
  kgText,
  lineHas,
  lineName,
  loadText,
  partHas,
  plural3,
  pmKey,
  textLines,
  workKg,
  type Block,
  type ExLookup,
  type ExLine,
  type Exercise,
  type Line,
  type Maxes,
  type VideoLink,
  type VideoRef,
  type WorkoutContent,
} from '@tg/domain';

/* Тренировка глазами клиента: группы строк блока, ключи 1ПМ, снимок с весами.
   Строки — через clientLine из @tg/domain; здесь только раскладка по группам. */

export interface MaxRow {
  id?: string;
  /** Кто внёс: свои записи клиент может поправить или удалить */
  source?: 'trainer' | 'client';
  key: string;
  reps: number;
  kg: number;
  date: string;
}

/** Текущие максимумы: последний по дате для каждого ключа (reps = 1 → 1ПМ) */
export function maxesOf(rows: MaxRow[] | undefined, reps = 1): Maxes {
  const pm: Maxes = {};
  for (const m of rows || []) if (m.reps === reps) pm[m.key] = m.kg;
  return pm;
}
/** История одного ключа по датам: несколько записей за день — последняя (поправка ошибки, а не рост) */
export const histOf = (rows: MaxRow[] | undefined, key: string, reps = 1) => {
  const out: [string, number][] = [];
  for (const m of rows || []) {
    if (m.key !== key || m.reps !== reps) continue;
    if (out.length && out[out.length - 1][0] === m.date) out[out.length - 1] = [m.date, m.kg];
    else out.push([m.date, m.kg]);
  }
  return out;
};

export const blockTitle = (b: Block) => blockName(b) || 'Блок';
export const visibleBlocks = (c: WorkoutContent | null | undefined) => (c?.blocks || []).filter(blockHas);

/* ── группы строк блока: формат комплекса, подряд идущие строки, сет, блок текстом ── */
export { blockRows as segments, type Row } from '@tg/domain';

/** Нагрузка строки для клиента: рабочий вес из % или абсолютная нагрузка */
export function loadOf(y: Line, pm: Maxes | null, ex: ExLookup) {
  const w = kgText(workKg(y, pm, ex));
  return w ? w + ' кг' : y.load && y.load.kind === 'abs' ? loadText(y.load) : '';
}

/** Связка делится на названия и «вес — подходы»: последние не разрываются при переносе */
export function chainParts(y: Line, pm: Maxes | null, ex: ExLookup) {
  if (y.t !== 'chain') return null;
  const prm = [loadOf(y, pm, ex), chainScheme(y)].filter(Boolean).join(' — ');
  return { names: lineName(y, ex) + (prm ? ':' : ''), prm };
}

export { clientLine };

/** Упражнения блока с карточкой в базе — для «Техники» */
export function blockExercises(b: Block, ex: ExLookup): Exercise[] {
  const out: Exercise[] = [];
  for (const y of b.items) {
    const lines = y.t === 'set' ? y.items : [y];
    for (const l of lines) {
      const parts = l.t === 'chain' ? l.parts.filter(partHas) : [l];
      for (const p of parts) {
        const e = p.exId && p.exId !== REST_ID ? ex(p.exId) : undefined;
        if (e && !out.includes(e)) out.push(e);
      }
    }
  }
  return out;
}
/** Пункт «Техники»: упражнение из базы и/или видео тренера (к строке текстом или к упражнению, VID-2).
    noGif — демонстрацию из базы тренер в этой тренировке убрал */
export interface TechItem {
  key: string;
  title: string;
  ex?: Exercise;
  video?: VideoRef | null;
  /** Ссылка тренера на ролик на видеохостинге (VID-3) — открывается там */
  link?: VideoLink | null;
  noGif?: boolean;
}
/** Видео строки: своё у строки (строка текстом), иначе — видео тренера к упражнению, если в строке его не убрали */
export const lineVideo = (l: Line, ex: ExLookup): VideoRef | null =>
  l.t === 'ex' ? l.video || (l.exId && !l.noVideo ? ex(l.exId)?.video : null) || null : null;
/** Ссылка строки на ролик: своя у строки, иначе — ссылка тренера к упражнению, если в строке её не убрали */
export const lineLink = (l: Line, ex: ExLookup): VideoLink | null =>
  l.t === 'ex' ? l.link || (l.exId && !l.noLink ? ex(l.exId)?.link : null) || null : null;

/** Пункт техники к одной строке (кнопка ▶ у строки) */
export function lineTech(l: Line, ex: ExLookup): TechItem | null {
  if (l.t !== 'ex') return null;
  const e = l.exId ? ex(l.exId) : undefined;
  const video = lineVideo(l, ex);
  const link = lineLink(l, ex);
  if (!video && !link) return null;
  return { key: l.id, title: e?.ru || String(l.raw || video?.title || 'Видео тренера'), ex: e, video, link, noGif: !!l.noGif };
}

export function blockTech(b: Block, ex: ExLookup): TechItem[] {
  const lines: Line[] = b.items.flatMap((y) => (y.t === 'set' ? y.items : [y]));
  /* По упражнению: видео и гифка видны, если хоть в одной строке блока их не убрали */
  const out: TechItem[] = blockExercises(b, ex).map((e) => {
    const own = lines.filter((l): l is ExLine => l.t === 'ex' && l.exId === e.id);
    const video = own.map((l) => lineVideo(l, ex)).find(Boolean) ?? (own.length ? null : e.video ?? null);
    const link = own.map((l) => lineLink(l, ex)).find(Boolean) ?? (own.length ? null : e.link ?? null);
    return { key: e.id, title: e.ru, ex: e, video, link, noGif: own.length > 0 && own.every((l) => l.noGif) };
  });
  for (const l of lines) {
    if (l.t !== 'ex' || l.exId || !(l.video || l.link)) continue;
    if (l.video && out.some((x) => x.video?.id === l.video!.id)) continue;
    if (!l.video && out.some((x) => x.link?.url === l.link!.url)) continue;
    out.push({ key: l.id, title: String(l.raw || l.video?.title || 'Видео тренера'), video: l.video, link: l.link });
  }
  return out;
}

/** Ключи 1ПМ блока — для «Рекорда»: только движения, от максимума которых считается вес */
export const blockPmKeys = (b: Block, ex: ExLookup) => [...new Set(blockExercises(b, ex).map(pmKey).filter(Boolean) as string[])];

/** Снимок для finishDay: для % посчитан и записан рабочий вес — история
    не пересчитывается новым 1ПМ. Исходный процент остаётся в поле pct. */
export function freeze(c: WorkoutContent, pm: Maxes, ex: ExLookup): WorkoutContent {
  const out: WorkoutContent = JSON.parse(JSON.stringify(c));
  const fix = (y: Line) => {
    if (!y.load || y.load.kind !== 'pct') return;
    const w = workKg(y, pm, ex);
    if (!w) return;
    (y as Line & { pct?: unknown }).pct = y.load;
    y.load = { kind: 'abs', unit: 'кг', from: w.lo, ...(w.hi != null ? { to: w.hi } : {}) };
  };
  for (const b of out.blocks) for (const y of b.items) (y.t === 'set' ? y.items : [y]).forEach(fix);
  return out;
}

/* ── план строки для ввода результата (наше дополнение к прототипу) ── */
export { linePlan as planOf, type LinePlan } from '@tg/domain';

export interface SetFact {
  kg?: number | null;
  reps?: number | null;
  done: boolean;
}

export const num = (v: string) => {
  const x = parseFloat(String(v).replace(',', '.'));
  return isFinite(x) && x >= 0 ? x : null;
};
